import { MouseBlocker } from "@lib/titan2/dist/game/control/MouseBlocker";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { OrientationType } from "@lib/titan2/dist/game/renderer/constant/RendererConstants";
import { AbstractViewProperties } from "@lib/titan2/dist/game/view/properties/AbstractViewProperties";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import { gsap } from "gsap";
import { PixiPlugin } from "gsap/all";
import * as PIXI from "pixi.js";
import { SlingoEvent } from "../../../controller/event/SlingoEvent";
import { TweenConstants } from "../constant/TweenConstants";
import { HelpSnapshot } from "../style/SlingoStyle";
import { AbstractGameView } from "./AbstractGameView";
gsap.registerPlugin(PixiPlugin);
/**
 * AvailableWinDisplay.ts
 */
export class AvailableWinDisplay extends AbstractGameView {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(SlingoEvent.AVAILABLE_WIN_START, this.handleAvailableWinStart);
        this.registerGame(SlingoEvent.AVAILABLE_WIN, this.handleAvailableWin);
        this.registerGame(SlingoEvent.AVAILABLE_WIN_CLEAR, this.handleAvailableWinClear);
        this.registerGame(SlingoEvent.AVAILABLE_WIN_END, this.handleAvailableWinEnd);
    }
    create() {
        super.create();
        this.createMouseBlocker();
        this.createAvailableWinText();
        this.createSnapshots();
        Logger.addObjectRef(this, "AvailableWinDisplay");
    }
    createMouseBlocker() {
        if (this.uiProperties.mouseBlockerProperties) {
            this.mouseBlocker = new MouseBlocker(this.landscapeProperties.mouseBlockerProperties, this.portraitProperties?.mouseBlockerProperties);
        }
    }
    createAvailableWinText() {
        this.availableWinTextHolder = new PIXI.Container();
        this.background = TitanSprite.createSprite(this.availableWinTextHolder, "background", this.landscapeProperties.backgroundProperties, this.portraitProperties?.backgroundProperties);
        this.availableWinLabel = ViewUtils.createField(this.availableWinTextHolder, this.landscapeProperties.availableWinLabelProperties, this.portraitProperties.availableWinLabelProperties);
        this.availableWinField = ViewUtils.createField(this.availableWinTextHolder, this.landscapeProperties.availableWinFieldProperties, this.portraitProperties.availableWinFieldProperties);
        this.availableWinTextHolder.position.copyFrom(this.uiProperties.availableWinTextHolderPosition);
        this.availableWinTextHolder.visible = false;
        this.addChild(this.availableWinTextHolder);
    }
    createSnapshots() {
        this.addPendingSnapshot(HelpSnapshot.AVAILABLE_WIN_DISPLAY_LANDSCAPE, this.availableWinTextHolder, null, this.prepareLandscapeSnapshot.bind(this), this.endSnapshot.bind(this));
        this.addPendingSnapshot(HelpSnapshot.AVAILABLE_WIN_DISPLAY_PORTRAIT, this.availableWinTextHolder, null, this.preparePortraitSnapshot.bind(this), this.endSnapshot.bind(this));
    }
    prepareLandscapeSnapshot() {
        this.forceRotate(OrientationType.LANDSCAPE);
        this.prepareSnapshot();
    }
    preparePortraitSnapshot() {
        this.forceRotate(OrientationType.PORTRAIT);
        this.prepareSnapshot();
    }
    prepareSnapshot() {
        const maxPayout = this.model.getPayoutConfig().getMaxPayout();
        const maxStake = this.model.getStake().getAmounts().reduce((pre, cur) => pre > cur ? pre : cur, 0);
        const amount = maxPayout * maxStake;
        this.availableWinField.setText(this.formatCurrency(amount));
        this.availableWinField.scale.set(1);
        this.availableWinTextHolder.alpha = 1;
        this.availableWinTextHolder.visible = true;
        //need to move things into the middle of the container to clone it correctly
        const offsetX = this.availableWinTextHolder.width / 2;
        this.availableWinField.x += offsetX;
        this.availableWinLabel.x += offsetX;
    }
    endSnapshot() {
        this.availableWinTextHolder.alpha = 0;
        this.availableWinTextHolder.visible = false;
        this.availableWinField.position.copyFrom(this.uiProperties.availableWinFieldProperties?.position || new PIXI.Point());
        this.availableWinLabel.position.copyFrom(this.uiProperties.availableWinLabelProperties?.position || new PIXI.Point());
        this.forceRotate(this.renderer.getOrientation());
    }
    showAvaliableWinOverlay() {
        if (this.mouseBlocker) {
            this.mouseBlocker.alpha = 0;
            this.addChildAt(this.mouseBlocker, 0);
            gsap.to(this.mouseBlocker, { duration: 0.25, alpha: 1 });
        }
    }
    hideAvaliableWinOverlay() {
        this.mouseBlocker && gsap.to(this.mouseBlocker, {
            duration: 0.25,
            alpha: 0,
            onComplete: () => {
                this.removeChild(this.mouseBlocker);
            }
        });
    }
    showAvailableWinText() {
        if (this.model.isStakeToSpin()) {
            return;
        }
        let availableWin = this.model.getAvailableWin();
        let winAmount = availableWin.getWinAmount();
        this.availableWinField.setText(this.formatCurrency(winAmount));
        this.availableWinTextHolder.scale.set(0);
        this.availableWinTextHolder.alpha = 1;
        this.availableWinTextHolder.visible = true;
        gsap.to(this.availableWinTextHolder, {
            pixi: { scale: 1, autoAlpha: 1 },
            duration: TweenConstants.AVAILABLE_WIN_SHOW_DURATION,
            ease: "back.out(0.5)"
        });
    }
    hideAvailableWinText() {
        if (this.model.isStakeToSpin()) {
            return;
        }
        gsap.to(this.availableWinTextHolder, {
            pixi: { autoAlpha: 0 },
            duration: TweenConstants.AVAILABLE_WIN_FADE_DURATION,
            ease: "none",
            onComplete: this.hideAvailableWinTextComplete,
            callbackScope: this
        });
    }
    hideAvailableWinTextComplete() { }
    handleAvailableWinStart() {
        this.showAvaliableWinOverlay();
    }
    handleAvailableWin() {
        this.showAvailableWinText();
        gsap.delayedCall(TweenConstants.AVAILABLE_WIN_POPUP_DURATION, () => this.hideAvailableWinText());
    }
    handleAvailableWinClear() {
        this.hideAvailableWinText();
    }
    handleAvailableWinEnd() {
        this.hideAvaliableWinOverlay();
        this.hideAvailableWinText();
    }
    handleRotate(event) {
        const data = event?.getCurrentTarget() || {};
        this.uiProperties = this.getCurrentProperties(data.forceOrientation);
        if (this.hasPortraitProperties) {
            this.layouts = [];
            this.applyProperties();
            this.resizeView(this.availableWinTextHolder, new AbstractViewProperties(this.uiProperties.availableWinTextHolderPosition));
            this.availableWinField.setText(this.availableWinField.getText(), true);
            this.resizeView(this.availableWinField, this.uiProperties.availableWinFieldProperties);
            this.availableWinLabel.setText(this.availableWinLabel.getText(), true);
            this.resizeView(this.availableWinLabel, this.uiProperties.availableWinLabelProperties);
        }
    }
}
//# sourceMappingURL=AvailableWinDisplay.js.map